import { Alerts } from 'constants/alerts';

import { useMemo, useState } from 'react';

import { useApiContext } from 'context/apiContext';
import { useListContext, WithListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { EmptyListAlert } from 'shared/mui/Alert/EmptyListAlert/EmptyListAlert';
import { FinanceUserAlert } from 'shared/mui/Alert/FinanceUserAlert/FinanceUserAlert';
import { SuccessReportAlert } from 'shared/mui/Alert/SuccessReportAlert/SuccessReportAlert';
import { Box } from 'shared/mui/Box';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterIdButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterIdChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterIdDialog } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialog';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { FilterIdTypes, useIdFilters } from 'shared/mui/Filter/hooks/useIdFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { Order } from 'types/embedded/order';
import { RefundChargebackReportSummary as RefundChargebackReportSummaryType } from 'types/embedded/refundChargebackReport';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { FILTER_RESET_VALUES } from './constants/filterResetValues';
import { RefundChargebackReportDatagrid } from './RefundChargebackReportDatagrid/RefundChargebackReportDatagrid';
import {
  RefundChargebackReportFilter,
  RefundChargebackReportListFilterValues,
} from './RefundChargebackReportFilter/RefundChargebackReportFilter';
import { RefundChargebackReportFilterChips } from './RefundChargebackReportFilter/RefundChargebackReportFilterChips';
import { RefundChargebackReportSummary } from './RefundChargebackReportSummary/RefundChargebackReportSummary';
import { RefundChargebackReportSummarySkeleton } from './RefundChargebackReportSummary/RefundChargebackReportSummarySkeleton';
import { getIdArrayFilterDefaultValue } from './utils/getIdArrayFilterDefaultValue';
import { getIdFilterDefaultValue } from './utils/getIdFilterDefaultValue';
import { transformFilterValues } from './utils/transformFilterValues';

type Props = {
  summary?: RefundChargebackReportSummaryType;
};

export const RefundChargebackReportListContent: React.FC<Props> = ({ summary }) => {
  const { filterValues } = useListContext();

  const isFiltersApplied = useMemo(() => Object.keys(filterValues).length > 0, [filterValues]);
  const [currentIdFilter, setCurrentIdFilter] = useState<FilterIdTypes>(FilterIdTypes.Id);
  const [isOpenIdDialog, setIsOpenIdDialog] = useState<boolean>(false);
  const [isShowSuccessAlert, setIsShowSuccessAlert] = useState<boolean>(true);

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<RefundChargebackReportListFilterValues>({
      mode: 'all',
      defaultValues: {
        appliedDate: [
          formatInputToTimestamp(filterValues?.createdAt?.gte),
          formatInputToTimestamp(filterValues?.createdAt?.lte),
        ],
        updatedDate: [
          formatInputToTimestamp(filterValues?.updatedAt?.gte),
          formatInputToTimestamp(filterValues?.updatedAt?.lte),
        ],
        timezone: filterValues.timezone || null,
        company: getIdFilterDefaultValue(filterValues?.company, 'companies'),
        merchant: getIdFilterDefaultValue(filterValues?.merchant, 'merchants'),
        provider: getIdFilterDefaultValue(filterValues?.provider, 'providers'),
        status: filterValues?.status || [],
        merchantAccount: getIdArrayFilterDefaultValue(
          filterValues?.merchantAccount,
          'merchant_accounts',
        ),
        gateway: getIdArrayFilterDefaultValue(filterValues?.gateway, 'gateways'),
        currency: getIdArrayFilterDefaultValue(filterValues?.currency, 'currencies'),
        method: getIdArrayFilterDefaultValue(filterValues?.method, 'methods'),
        conversionCurrency: getIdFilterDefaultValue(filterValues?.conversionCurrency, 'currencies'),
        summary: Boolean(filterValues?.summary),
        impactToBalance: filterValues?.impactToBalance || null,
      },
      resetValues: FILTER_RESET_VALUES,
    });

  const {
    setAppliedFilter: setAppliedIdFilter,
    onSubmit: onSubmitIds,
    onReset: onResetIds,
    appliedIdFilters,
    form: formId,
  } = useIdFilters({
    defaultValues: {
      merchantReference: filterValues?.merchantReference || [],
      id: filterValues?.id || [],
      providerReferenceId: filterValues?.providerReferenceId || [],
    },
    resetValues: {
      merchantReference: [],
      id: [],
      providerReferenceId: [],
    },
  });

  useSyncFilter<RefundChargebackReportListFilterValues>({
    appliedFilters,
    appliedIdFilters,
    transform: transformFilterValues,
  });

  const formValuesWatched = form.watch();
  const idsFormValuesWatched = formId.watch();
  const isFormValuesExist = Object.values(cleanEmpty(formValuesWatched)).length > 0;
  const isIdFormValuesExist = Object.values(cleanEmpty(idsFormValuesWatched)).length > 0;

  const { user } = useApiContext();

  const getReportDate = () => {
    if (appliedFilters?.appliedDate?.[0] && appliedFilters?.appliedDate?.[1]) {
      return {
        gte: formatInputToTimestamp(appliedFilters?.appliedDate?.[0], { unit: 'seconds' }),
        lte: formatInputToTimestamp(appliedFilters?.appliedDate?.[1], { unit: 'seconds' }),
      };
    } else {
      return {
        gte: formatInputToTimestamp(appliedFilters?.updatedDate?.[0], { unit: 'seconds' }),
        lte: formatInputToTimestamp(appliedFilters?.updatedDate?.[1], { unit: 'seconds' }),
      };
    }
  };

  return (
    <WithListContext
      render={({ isLoading, data: orders, total, isFetching }) => {
        const notFoundIds = appliedIdFilters.id?.filter(
          (id) => id && !orders?.some((order) => order?.id === Number(id)),
        );
        const notFoundMerchantIds = appliedIdFilters.merchantReference?.filter(
          (id) => !orders?.some((order) => order?.merchantReference === id),
        );
        const notFoundProviderIds = appliedIdFilters.providerReferenceId?.filter(
          (id) => !orders?.some((order) => order?.providerReferenceId === id),
        );
        const isShowSummary = !!orders?.length && !!summary;

        function renderAlert() {
          if (user.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
            return <FinanceUserAlert firstName={user.firstName} lastName={user.lastName} />;
          }

          if (!!orders?.length && isShowSuccessAlert) {
            return (
              <SuccessReportAlert
                date={getReportDate()}
                onClose={() => setIsShowSuccessAlert(false)}
                timezone={appliedFilters?.timezone || DEFAULT_TIMEZONE}
              />
            );
          }

          if (!isFiltersApplied) {
            return <EmptyListAlert onClick={toggleDrawer(true)} />;
          }

          return <></>;
        }

        return (
          <>
            <FilterToolbar
              leftActionsSlot={[
                <FilterIdButton
                  disabled={isFormValuesExist}
                  key="filter-by-id"
                  onClick={() => {
                    setIsOpenIdDialog(true);
                  }}
                />,
                <FilterButton
                  disabled={isIdFormValuesExist}
                  key="filter"
                  onClick={toggleDrawer(true)}
                />,
                <FilterColumnsButton
                  disabled={!orders?.length && !isFiltersApplied}
                  key="select-column"
                />,
              ]}
            />
            <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
              <FormProvider {...form}>
                <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
                  <FilterDrawerContent>
                    <RefundChargebackReportFilter />
                  </FilterDrawerContent>
                  <FilterDrawerActionsButtons>
                    <FilterResetButton label="Очистить" onReset={onReset} />
                    <FilterApplyButton type="submit" />
                  </FilterDrawerActionsButtons>
                </form>
              </FormProvider>
            </FilterDrawer>
            <FilterChipsToolbar appliedFilters={appliedFilters} appliedIdFilters={appliedIdFilters}>
              <FilterContextProvider
                appliedFilters={appliedFilters}
                onReset={onReset}
                setAppliedFilter={setAppliedFilter}
              >
                <FormProvider {...form}>
                  <RefundChargebackReportFilterChips />
                </FormProvider>
              </FilterContextProvider>
              <FilterContextProvider
                appliedFilters={appliedIdFilters}
                setAppliedFilter={setAppliedIdFilter}
              >
                <FormProvider {...formId}>
                  <FilterIdChip
                    label="ID Kubera"
                    limitTags={2}
                    name="id"
                    onClick={() => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(FilterIdTypes.Id);
                    }}
                  />
                  <FilterIdChip
                    label="ID Мерчантов"
                    limitTags={2}
                    name="merchantReference"
                    onClick={() => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(FilterIdTypes.Merchant);
                    }}
                  />
                  <FilterIdChip
                    label="ID Провайдеров"
                    limitTags={2}
                    name="providerReferenceId"
                    onClick={() => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(FilterIdTypes.Provider);
                    }}
                  />
                  <FilterClearButton
                    key={`clear-${currentIdFilter}`}
                    onClick={() => {
                      onReset();
                      onResetIds(currentIdFilter);
                    }}
                    visible={isFiltersApplied}
                  />
                </FormProvider>
              </FilterContextProvider>
            </FilterChipsToolbar>
            <FormProvider {...formId}>
              <FilterIdDialog
                currentIdFilter={currentIdFilter}
                notFoundIds={notFoundIds}
                notFoundMerchantIds={notFoundMerchantIds}
                notFoundProviderIds={notFoundProviderIds}
                omit={[FilterIdTypes.Any]}
                onChange={(currentFilter) => setCurrentIdFilter(currentFilter)}
                onClose={() => setIsOpenIdDialog(false)}
                onReset={onResetIds}
                onSubmit={onSubmitIds}
                open={isOpenIdDialog}
              />
            </FormProvider>
            {renderAlert()}
            <Box sx={{ marginBottom: '24px' }}>
              {isLoading && !!filterValues?.summary && <RefundChargebackReportSummarySkeleton />}
              {isShowSummary && isFiltersApplied && (
                <RefundChargebackReportSummary summary={summary} />
              )}
              <RefundChargebackReportDatagrid
                empty={isFiltersApplied ? undefined : <></>}
                isLoading={!!isLoading}
                orders={isFiltersApplied && !isFetching ? (orders as Order[]) || [] : []}
                timezone={filterValues?.timezone}
                total={isFiltersApplied && !isFetching ? total || 0 : 0}
              />
            </Box>
          </>
        );
      }}
    />
  );
};
